import React from 'react'
import styled from 'styled-components'
import { ink, line, radius, font } from 'features/dashboard/tokens'

/**
 * Multi-series line chart. Single-series usage is most common for the
 * dashboard's compliance trend; the component takes an array of series
 * so we can drop in a "target" reference line later.
 *
 * Props:
 *   series: [{ name, colour, data: [{ date, score }] }]
 *   height
 *   yMin, yMax — optional, defaults to data-range
 */
const TrendLine = ({ series = [], height = 200, yMin, yMax }) => {
  const wrapRef = React.useRef(null)
  const [w, setW] = React.useState(800)

  React.useLayoutEffect(() => {
    const el = wrapRef.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    const measure = () => {
      const width = el.clientWidth
      if (width > 0) setW(width)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const sane = series.filter(s => Array.isArray(s.data) && s.data.length > 0)
  if (!sane.length) {
    return <Empty>Trends will appear once daily snapshots have accrued.</Empty>
  }

  const h = height
  const pad = { l: 36, r: 12, t: 12, b: 24 }

  // Compliance scores are inherently 0–100 percentages; anchor the y-axis
  // there by default so the ticks aren't misleading when the data happens
  // to be clustered in a narrow band. Caller can override with yMin/yMax.
  const lo = yMin ?? 0
  const hi = yMax ?? 100

  const xs = sane[0].data.map(d => d.date)
  const x = i => pad.l + (i / (xs.length - 1 || 1)) * (w - pad.l - pad.r)
  const y = v => pad.t + (1 - (v - lo) / (hi - lo || 1)) * (h - pad.t - pad.b)

  return (
    <Wrap ref={wrapRef}>
      <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} style={{ display: 'block', maxWidth: '100%' }}>
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
          const yy = pad.t + t * (h - pad.t - pad.b)
          const v = Math.round(hi - t * (hi - lo))
          return (
            <g key={i}>
              <line x1={pad.l} y1={yy} x2={w - pad.r} y2={yy} stroke={line.base} strokeDasharray='4 4' />
              <text x={pad.l - 8} y={yy + 4} fontSize='10' fill={ink.mute} textAnchor='end'>{v}%</text>
            </g>
          )
        })}
        {xs.map((m, i) => (
          <text key={i} x={x(i)} y={h - 6} fontSize='10' fill={ink.mute} textAnchor='middle'>
            {labelFor(m)}
          </text>
        ))}
        {sane.map((s, si) => {
          const pts = s.data
            .map((d, i) => d.score === null ? null : `${x(i).toFixed(1)},${y(d.score).toFixed(1)}`)
            .filter(Boolean)
          if (!pts.length) return null
          const path = `M${pts.join(' L')}`
          return (
            <g key={si}>
              <path d={path} fill='none' stroke={s.colour || '#D24724'} strokeWidth='2.4'
                strokeLinecap='round' strokeLinejoin='round' />
              {s.data.map((d, i) => d.score !== null ? (
                <circle key={i} cx={x(i)} cy={y(d.score)} r='3' fill='#fff'
                  stroke={s.colour || '#D24724'} strokeWidth='2' />
              ) : null)}
            </g>
          )
        })}
      </svg>
      <Legend>
        {sane.map((s, i) => (
          <LegendItem key={i}>
            <Swatch style={{ background: s.colour || '#D24724' }} />
            <span>{s.name}</span>
          </LegendItem>
        ))}
      </Legend>
    </Wrap>
  )
}

const labelFor = isoDate => {
  if (!isoDate) return ''
  const d = new Date(isoDate)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('en-GB', { month: 'short' })
}

export default TrendLine

const Wrap = styled.div``

const Empty = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 22px;
  font-size: ${font.body};
  color: ${({ theme }) => theme.colors.grey[800]};
  border: 1px dashed ${line.base};
  border-radius: ${radius.md};
  text-align: center;
`

const Legend = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
`

const LegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: ${font.small};
  color: ${({ theme }) => theme.colors.haiti};
`

const Swatch = styled.span`
  width: 10px;
  height: 10px;
  border-radius: ${radius.round};
`
